import React, {MouseEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react";

import {
    ACard,
    ADataGrid,
    ADataGridFilter,
    PagedSearchParams,
    Period,
} from "@atiautomacao/ati-ui-library";
import {
    Box,
    Grid,
    Link,
    LinearProgress,
    linearProgressClasses,
    styled,
    Typography,
    useTheme,
} from "@mui/material";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {RootState} from "../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {
    clear,
    Performance,
    PowerStationPerformanceRow,
    searchAnalisysData,
    searchPowerStations,
} from "./PerformanceAnalysisReducer";
import {usePowerStationNavigation} from '../../../Shared/Hooks/usePowerStationNavigation';
import {performanceAnalysisColumns} from "./PerformanceAnalysisConfig";
import useInterval from "../../../Shared/Hooks/useInterval";
import PowerIrradianceMiniChart from "./PowerIrradianceMiniChart";
import {Pill, pillColors} from "../Unavailability/Pill";
import {getAvailabilityTone, getPerformanceRatioTone} from "../../../Utils/PerformanceRatioUtils";
import {PlantCommunicationIndicator} from "./components/PlantCommunicationIndicator";
import {PlantAlarmSummaryCell} from "./components/PlantAlarmSummaryCell";
import {
    getActiveCommunicationSortDirection,
    normalizePerformanceAnalysisSortFilters,
    sortPowerStationPerformanceRows,
} from "./performanceAnalysisSort";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";

const getTodayPeriod = (): { fromDateTime: Date; toDateTime: Date } => {
    const toDateTime = new Date();
    const fromDateTime = new Date();
    fromDateTime.setHours(0, 0, 0, 0);
    return {fromDateTime, toDateTime};
};

// SGD-3729: janela de busca do gráfico Potência x Irradiância restrita a 04h-20h
const getChartDataPeriod = (): { fromDateTime: Date; toDateTime: Date } => {
    const fromDateTime = new Date();
    fromDateTime.setHours(4, 0, 0, 0);
    const toDateTime = new Date();
    toDateTime.setHours(20, 0, 0, 0);
    return {fromDateTime, toDateTime};
};

const buildFiltersKey = (filters: Array<ADataGridFilter>): string =>
    JSON.stringify(filters.map((f) => [f.name, f.condition, f.value, f.sort]));

export type PerformanceAnalysisPageProps = {
    selectedPowerStationIds?: Array<number | string>;
    availablePowerStationIds?: Array<number | string>;
    isFilterInitialized?: boolean;
    coordinatedDailyRefresh?: boolean;
    coordinatedRefreshGeneration?: number;
    coordinatedPeriod?: { fromDateTime: Date; toDateTime: Date };
};

const PerformanceAnalysisPage = ({
    selectedPowerStationIds,
    availablePowerStationIds,
    isFilterInitialized = true,
    coordinatedDailyRefresh = false,
    coordinatedRefreshGeneration,
    coordinatedPeriod,
}: PerformanceAnalysisPageProps): ReactElement => {
    const theme = useTheme();

    const dispatch = useAppDispatch();
    const {entities, loading, powerStations, errorMessage} = useAppSelector((state: RootState) => state.performanceAnalysis);
    const totalOfRecords = useAppSelector((state: RootState) => state.performanceAnalysis.totalOfRecords);
    const navigationToPowerStation = usePowerStationNavigation();
    const entityMap = useRef(new Map<number, Performance>()).current;
    const [gridColumns] = useState(() => performanceAnalysisColumns.map((column) => ({
        ...column,
        filter: column.filter ? {...column.filter} : undefined,
    })));

    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
    const [pagedSearchParams, setPagedSearchParams] = useState(
        new PagedSearchParams(extractFiltersFromColumns(performanceAnalysisColumns), 0, userConfig.sizePagePerformanceAnalysis)
    );

    const handleOnFiltersChange = useCallback((
        dataGridFilters: Array<ADataGridFilter>,
        page: number,
        pageSize: number
    ) => {
        const normalizedFilters = normalizePerformanceAnalysisSortFilters(dataGridFilters);
        setPagedSearchParams((prev) => {
            const plantFilters = prev.filters.filter((filter) => filter.name === "powerStationId");
            const gridFilters = normalizedFilters.filter((filter) => filter.name !== "powerStationId");
            const nextFilters = [...gridFilters, ...plantFilters];
            // SGD-3849: o ADataGrid repete o callback ao receber novas colunas; sem mudança real, não refaz a busca
            if (prev.page === page && prev.size === pageSize && buildFiltersKey(prev.filters) === buildFiltersKey(nextFilters)) {
                return prev;
            }
            return new PagedSearchParams(nextFilters, page, pageSize);
        });
    }, []);

    const orderPowerStationsForGrid = useCallback((stations: PowerStationPerformanceRow[]) => {
        const communicationSortDirection = getActiveCommunicationSortDirection(pagedSearchParams.filters);
        return communicationSortDirection
            ? sortPowerStationPerformanceRows(stations, communicationSortDirection)
            : stations;
    }, [pagedSearchParams.filters]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [standaloneRefreshGeneration, setStandaloneRefreshGeneration] = useState(0);
    const standalonePeriodRef = useRef(getTodayPeriod());
    const listRequestSequenceRef = useRef(0);
    const analysisRequestSequenceRef = useRef(0);
    const lastListRequestSignatureRef = useRef<string | null>(null);

    const plantFilter = useMemo<ADataGridFilter | null>(() => {
        if (!isFilterInitialized) {
            return null;
        }
        const selected = (selectedPowerStationIds ?? []).map((id) => Number(id));
        const available = (availablePowerStationIds ?? []).map((id) => Number(id));
        if (selected.length === 0) {
            return null;
        }
        if (available.length > 0 && selected.length === available.length) {
            return null;
        }
        return {
            name: "powerStationId",
            condition: "equals",
            value: selected.join("~"),
            sort: "none",
        };
    }, [isFilterInitialized, selectedPowerStationIds, availablePowerStationIds]);

    const selected = (selectedPowerStationIds ?? []).map((id) => Number(id));
    const available = (availablePowerStationIds ?? []).map((id) => Number(id));
    const emptySelection = isFilterInitialized && selected.length === 0 && available.length > 0;
    const expectsPlantFilter = isFilterInitialized && !emptySelection && plantFilter !== null;
    const hasPlantFilter = pagedSearchParams.filters.some((filter) => filter.name === "powerStationId");

    const shouldFetchList = isFilterInitialized
        && !emptySelection
        && (!expectsPlantFilter || hasPlantFilter);

    const plantFilterKey = plantFilter?.value ?? "";

    useEffect(() => {
        setPagedSearchParams((prev) => {
            const withoutPlant = prev.filters.filter((filter) => filter.name !== "powerStationId");
            const nextFilters = plantFilter ? [...withoutPlant, plantFilter] : withoutPlant;
            const previousPlantFilter = prev.filters.find((filter) => filter.name === "powerStationId");
            if ((previousPlantFilter?.value ?? "") === plantFilterKey) {
                return prev;
            }
            return new PagedSearchParams(nextFilters, 0, prev.size);
        });
        entityMap.clear();
    }, [plantFilterKey, plantFilter, entityMap]);

    const handleNavigation = (event: MouseEvent<HTMLElement>, powerPlantId: number): void => {
        event.preventDefault();
        navigationToPowerStation(powerPlantId);
    };

    const BorderLinearProgress = styled(LinearProgress)(({theme, value}) => ({
        height: 6,
        borderRadius: 5,
        [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: theme.palette.grey,
        },
        [`& .${linearProgressClasses.bar}`]: {
            borderRadius: 5,
            backgroundColor: pillColors[getPerformanceRatioTone(value ?? 0)][theme.palette.mode === 'light' ? 'light' : 'dark'].fg,
        },
    }));

    const isLightMode = () => theme.palette.mode === 'light';

    function LinearProgressWithLabel({value}: {value: number}) {
        const tone = getPerformanceRatioTone(value ?? 0);
        return (
            <Box sx={{width: 80}}>
                <Typography variant="body2" fontWeight={700} color={pillColors[tone][theme.palette.mode === 'light' ? 'light' : 'dark'].fg}>
                    {`${Math.round(value)}%`}
                </Typography>
                <Box sx={{width: '100%', mt: 0.5}}>
                    <BorderLinearProgress variant="determinate" value={value}/>
                </Box>
            </Box>
        );
    }

    function formatInstantIrradiance(element: number | null | undefined): string {
        return element !== null && element !== undefined ? Math.round(element).toString() : "-";
    }

    const GeneratedInjected = (expected: number | null | undefined, generated: number, injected: number) => {
        return (
            <Box>
                <Typography variant="subtitle1" fontSize={12} color={isLightMode() ? "rgba(0, 0, 0, 0.87)" : "#fff"} style={{fontWeight: 400}}>Esperada: <label style={{color: isLightMode() ? "rgba(0, 0, 0, 0.87)" : "#fff", fontWeight: 700}}>{expected !== null && expected !== undefined ? `${expected.toFixed(2) + " MWh "}` : "- "} </label></Typography>
                <Typography variant="subtitle1" fontSize={12} color={isLightMode() ? "rgba(0, 0, 0, 0.87)" : "#fff"} style={{fontWeight: 400}}>Gerada: <label style={{color: isLightMode() ? "rgba(0, 0, 0, 0.87)" : "#fff", fontWeight: 700}}>{generated ? `${generated.toFixed(2) + " MWh "}` : "- "} </label></Typography>
                <Typography variant="subtitle1" fontSize={12} color={isLightMode() ? "rgba(0, 0, 0, 0.87)" : "#fff"} style={{fontWeight: 400}}>Injetada: <label style={{color: isLightMode() ? "rgba(0, 0, 0, 0.87)" : "#fff", fontWeight: 700}}>{injected ? `${injected.toFixed(2) + " MWh "}` : "- "} </label></Typography>
            </Box>
        );
    };

    const structureRow = useCallback((entitiesValues: Array<PowerStationPerformanceRow>) => {
        const entityRows: Array<Record<string, unknown>> = [];
        entitiesValues.forEach(element => {
            const chartEntity = entityMap.get(element.powerStationId);

            entityRows.push({
                powerStationName: <Link href="#" onClick={(event) => handleNavigation(event, element.powerStationId)} underline="hover">{element.powerStationName}</Link>,
                communicationState: (
                    <Box sx={{px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 106}}>
                        <PlantCommunicationIndicator state={element.communicationState}/>
                    </Box>
                ),
                alarmSummary: (
                    <Box sx={{px: 1.25, display: 'flex', alignItems: 'center', minHeight: 106}}>
                        <PlantAlarmSummaryCell
                            powerStationId={element.powerStationId}
                            powerStationName={element.powerStationName}
                            communicationState={element.communicationState}
                            alarmSummary={element.alarmSummary}
                        />
                    </Box>
                ),
                powerCapacityNominal: element.powerCapacityNominal ? parseInt(element.powerCapacityNominal.toString()) : " ",
                instantPower: <div style={{width: '90%', display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center'}}>
                    <Box>{element.instantPower !== null && element.instantPower !== undefined ? parseFloat(element.instantPower.toString()).toFixed(2) : "-"}</Box>
                </div>,
                powerIrradiance: chartEntity && chartEntity.instantPowerList ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 8px', width: '188px'}}>
                        <PowerIrradianceMiniChart dataInstantPower={chartEntity.instantPowerList} maxPower={chartEntity.maxPower} powerStation={element.powerStationName}/>
                    </Box>
                ) : null,
                instantIrradiance: <Box sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px'}}>{formatInstantIrradiance(element.instantIrradiance)}</Box>,
                generatedEnergySum: (
                    <Box>
                        {GeneratedInjected(
                            element.expectedEnergyCorrectedByIrradiationSum,
                            element.generatedEnergySum,
                            element.injectedEnergySum
                        )}
                    </Box>
                ),
                performanceRatio: <LinearProgressWithLabel value={element.performanceRatio || 0}/>,
                availability: (element.availability === null || element.availability === undefined)
                    ? "-"
                    : <Pill label={`${parseInt(element.availability.toString())}%`} tone={getAvailabilityTone(element.availability)}/>,
            });
        });
        setRows(entityRows);
    }, [entityMap, theme.palette.mode]);

    const fetchPowerStations = useCallback((
        params: PagedSearchParams,
        requestPeriod: {fromDateTime: Date; toDateTime: Date}
    ) => {
        const periodWeek: Period = {groupBy: "week", ...requestPeriod};
        return dispatch(searchPowerStations({period: periodWeek, actualDay: periodWeek, params}));
    }, [dispatch]);

    useEffect(() => {
        if (!shouldFetchList) {
            return;
        }
        const requestPeriod = coordinatedDailyRefresh && coordinatedPeriod
            ? coordinatedPeriod
            : standalonePeriodRef.current;
        const requestSignature = JSON.stringify({
            start: requestPeriod.fromDateTime.getTime(),
            end: requestPeriod.toDateTime.getTime(),
            params: pagedSearchParams.toURLSearchParams().toString(),
            refreshGeneration: coordinatedDailyRefresh
                ? coordinatedRefreshGeneration ?? 0
                : standaloneRefreshGeneration,
        });
        if (lastListRequestSignatureRef.current === requestSignature) {
            return;
        }
        lastListRequestSignatureRef.current = requestSignature;
        const requestSequence = listRequestSequenceRef.current + 1;
        listRequestSequenceRef.current = requestSequence;
        void fetchPowerStations(pagedSearchParams, requestPeriod).finally(() => {
            if (listRequestSequenceRef.current === requestSequence) {
                setIsFirstLoad(false);
            }
        });
    }, [
        shouldFetchList,
        pagedSearchParams,
        coordinatedDailyRefresh,
        coordinatedRefreshGeneration,
        coordinatedPeriod,
        standaloneRefreshGeneration,
        fetchPowerStations,
    ]);

    useInterval(() => {
        if (coordinatedDailyRefresh || !shouldFetchList) {
            return;
        }
        standalonePeriodRef.current = getTodayPeriod();
        setStandaloneRefreshGeneration((value) => value + 1);
    }, userConfig.mainUpdateInterval);

    useEffect(() => {
        if (entities && entities.length > 0) {
            entities.forEach(entity => {
                entityMap.set(entity.powerStationId, entity);
            });
            if (powerStations && powerStations.length > 0) {
                structureRow(orderPowerStationsForGrid(powerStations));
            }
        }
    }, [entities, powerStations, orderPowerStationsForGrid, structureRow]);

    useEffect(() => {
        if (powerStations && powerStations.length > 0) {
            structureRow(orderPowerStationsForGrid(powerStations));
            const chartDataPeriod: Period = {groupBy: "week", ...getChartDataPeriod()};
            const requestSequence = analysisRequestSequenceRef.current + 1;
            analysisRequestSequenceRef.current = requestSequence;
            setIsLoadingData(true);
            void dispatch(searchAnalisysData({
                powerStationIds: powerStations.map(ps => ps.powerStationId),
                period: chartDataPeriod,
                actualDay: chartDataPeriod,
            })).finally(() => {
                if (analysisRequestSequenceRef.current === requestSequence) {
                    setIsLoadingData(false);
                }
            });
        } else {
            structureRow([]);
            setIsLoadingData(false);
        }
    }, [powerStations, orderPowerStationsForGrid, structureRow, dispatch]);

    useEffect(() => {
        dispatch(clear());
        setRows([]);
    }, [dispatch]);

    const showFilteredEmptyState =
        isFilterInitialized
        && shouldFetchList
        && !loading
        && !isLoadingData
        && (powerStations?.length ?? 0) === 0;

    const showErrorState = !!errorMessage && !loading && (powerStations?.length ?? 0) === 0;

    return (
        <Grid container>
            <Grid item xs={12}>
                <ACard
                    styleProps={{
                        headerStyle: {
                            display: 'none',
                            backgroundColor: 'rgb(247, 247, 247)'
                        },
                        footerStyle: {padding: 18, paddingTop: 0},
                        cardStyle: {padding: 0, borderRadius: '8px'},
                        contentStyle: {padding: 0},
                    }}
                >
                    {showErrorState ? (
                        <Box sx={{padding: 4}} data-testid="performance-analysis-error">
                            <DataNotFound message={errorMessage}/>
                        </Box>
                    ) : showFilteredEmptyState ? (
                        <Box sx={{padding: 4}} data-testid="performance-analysis-empty">
                            <DataNotFound message="Dados não encontrados!"/>
                        </Box>
                    ) : (
                        <ADataGrid
                            tableContainerStyle={{padding: 0}}
                            hideSelection={true}
                            ariaLabel={'overview-performance-analysis'}
                            hideFilters={true}
                            columns={gridColumns}
                            rows={rows}
                            page={pagedSearchParams.page}
                            loading={loading && !isLoadingData && isFirstLoad}
                            totalOfRecords={totalOfRecords || 0}
                            rowsPerPage={pagedSearchParams.size}
                            onFiltersChange={handleOnFiltersChange}
                            showSortOptions={true}
                        />
                    )}
                </ACard>
            </Grid>
        </Grid>
    );
};

export default PerformanceAnalysisPage;
